import { useState, useMemo } from "react";
import { Pencil, Trash2, ChevronDown, BookOpen, Search } from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { ModalSairSemSalvar } from "@/shared/components/ModalSairSemSalvar";
import { Button } from "@/shared/components/Button";
import { Paginacao } from "@/shared/components/Paginacao";
import { useAuth } from "@/core/hooks/useAuth";
import { FormularioCategoria, type FormularioCategoriaValores } from "@/modules/biblioteca/components/FormularioCategoria";
import { useCategorias, useCriarCategoria, useAtualizarCategoria, useExcluirCategoria } from "@/modules/biblioteca/hooks/useCategorias";
import { useLivros } from "@/modules/biblioteca/hooks/useLivros";
import type { Categoria } from "@/modules/biblioteca/services/categoriasService";

const ITENS_POR_PAGINA = 10;

export function GerenciarCategorias() {
  // UX-01 (auditoria 20/07/2026): mesmo ajuste de GerenciarLivros.tsx — os
  // botões só aparecem para quem gerencia o módulo Biblioteca.
  const { podeGerenciarBiblioteca } = useAuth();
  const { data: categorias, isLoading } = useCategorias();
  const { data: livros } = useLivros();
  const criarCategoria = useCriarCategoria();
  const atualizarCategoria = useAtualizarCategoria();
  const excluirCategoria = useExcluirCategoria();

  const [modalAberto, setModalAberto] = useState(false);
  const [categoriaEmEdicao, setCategoriaEmEdicao] = useState<Categoria | null>(null);
  const [erroExclusao, setErroExclusao] = useState<string | null>(null);
  const [pesquisa, setPesquisa] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [categoriaParaExcluir, setCategoriaParaExcluir] = useState<Categoria | null>(null);
  const [categoriaExpandida, setCategoriaExpandida] = useState<string | null>(null);
  const [erroFormulario, setErroFormulario] = useState<string | null>(null);
  const [formularioSujo, setFormularioSujo] = useState(false);
  const [confirmandoFechamento, setConfirmandoFechamento] = useState(false);

  // Mapa categoria_id -> lista de livros, calculado a partir dos livros já
  // carregados (sem query extra ao banco). UX-06: um livro pode estar em
  // mais de uma categoria agora (livros_categorias N:N), então cada livro
  // pode aparecer no mapa de mais de uma categoria.
  const livrosPorCategoria = useMemo(() => {
    const mapa = new Map<string, { id: string; nome_livro: string }[]>();
    (livros ?? []).forEach((livro) => {
      livro.categorias.forEach((categoria) => {
        const lista = mapa.get(categoria.id) ?? [];
        lista.push({ id: livro.id, nome_livro: livro.nome_livro });
        mapa.set(categoria.id, lista);
      });
    });
    return mapa;
  }, [livros]);

  const categoriasFiltradas = useMemo(() => {
    if (!categorias) return categorias;
    const termo = pesquisa.trim().toLowerCase();
    if (!termo) return categorias;
    return categorias.filter((c) => c.nome.toLowerCase().includes(termo));
  }, [categorias, pesquisa]);

  // Resumo para quem gerencia a biblioteca, igual ao do Acervo de livros: quantas categorias estão
  // cadastradas (e quantas aparecem com a pesquisa atual).
  const totalCategorias = categorias?.length ?? 0;
  const filtrando = pesquisa.trim() !== "";

  const [pesquisaAnterior, setPesquisaAnterior] = useState(pesquisa);
  if (pesquisa !== pesquisaAnterior) { setPesquisaAnterior(pesquisa); setPaginaAtual(1); }

  const totalPaginas = Math.max(1, Math.ceil((categoriasFiltradas?.length ?? 0) / ITENS_POR_PAGINA));
  const categoriasPaginadas = useMemo(() => {
    if (!categoriasFiltradas) return categoriasFiltradas;
    const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
    return categoriasFiltradas.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [categoriasFiltradas, paginaAtual]);

  function abrirCriacao() { setCategoriaEmEdicao(null); setErroFormulario(null); setFormularioSujo(false); setModalAberto(true); }
  function abrirEdicao(cat: Categoria) { setCategoriaEmEdicao(cat); setErroFormulario(null); setFormularioSujo(false); setModalAberto(true); }

  function tentarFecharModal() {
    if (formularioSujo) {
      setConfirmandoFechamento(true);
    } else {
      fecharModalDeVerdade();
    }
  }

  function fecharModalDeVerdade() {
    setModalAberto(false);
    setErroFormulario(null);
    setFormularioSujo(false);
    setConfirmandoFechamento(false);
  }

  async function handleSubmit(dados: FormularioCategoriaValores) {
    setErroFormulario(null);
    try {
      if (categoriaEmEdicao) {
        await atualizarCategoria.mutateAsync({ id: categoriaEmEdicao.id, nome: dados.nome });
      } else {
        await criarCategoria.mutateAsync(dados.nome);
      }
      setModalAberto(false);
      setFormularioSujo(false);
    } catch (erro) {
      setErroFormulario(erro instanceof Error ? erro.message : "Não foi possível salvar a categoria.");
    }
  }

  async function confirmarExclusao() {
    if (!categoriaParaExcluir) return;
    setErroExclusao(null);
    try {
      await excluirCategoria.mutateAsync(categoriaParaExcluir.id);
      setCategoriaParaExcluir(null);
    } catch (erro) {
      setCategoriaParaExcluir(null);
      setErroExclusao(erro instanceof Error ? erro.message : "Não foi possível excluir a categoria.");
    }
  }

  return (
    <>
      <main className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-brand-verde">Categorias</h1>
          {podeGerenciarBiblioteca && (
            <Button onClick={abrirCriacao} className="bg-brand-verde text-white">+ Nova categoria</Button>
          )}
        </div>

        {erroExclusao && (
          <p role="alert" className="mt-4 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">{erroExclusao}</p>
        )}

        <div className="relative mt-4">
          <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="search"
            placeholder="Pesquisar categoria..."
            value={pesquisa}
            onChange={(e) => setPesquisa(e.target.value)}
            className="w-full rounded-xl border border-border bg-surface py-3 pl-11 pr-4 text-sm text-text placeholder:text-muted focus:border-brand-verde focus:outline-none focus:ring-1 focus:ring-brand-verde"
          />
        </div>

        {podeGerenciarBiblioteca && !isLoading && (
          <p className="mt-4 text-sm text-muted" aria-live="polite">
            {filtrando ? (
              <>
                <span className="font-semibold text-text">{categoriasFiltradas?.length ?? 0}</span> de{" "}
                {totalCategorias} {totalCategorias === 1 ? "categoria cadastrada" : "categorias cadastradas"}
              </>
            ) : (
              <>
                <span className="font-semibold text-text">{totalCategorias}</span>{" "}
                {totalCategorias === 1 ? "categoria cadastrada" : "categorias cadastradas"}
              </>
            )}
          </p>
        )}

        <div className="mt-4 overflow-hidden rounded-xl border border-border bg-surface">
          {isLoading ? (
            <p className="p-6 text-sm text-muted">Carregando...</p>
          ) : !categoriasFiltradas?.length ? (
            <p className="p-6 text-sm text-muted">Nenhuma categoria cadastrada.</p>
          ) : (
            <ul>
              {categoriasPaginadas?.map((cat, idx) => {
                const livrosDaCategoria = livrosPorCategoria.get(cat.id) ?? [];
                const expandida = categoriaExpandida === cat.id;
                return (
                  <li key={cat.id} className={idx > 0 ? "border-t border-border" : ""}>
                    <div className="flex items-center gap-3 px-4 py-3">
                      <button
                        onClick={() => setCategoriaExpandida(expandida ? null : cat.id)}
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                      >
                        <p className="truncate text-sm font-medium text-text">{cat.nome}</p>
                        {livrosDaCategoria.length > 0 && (
                          <span className="flex shrink-0 items-center gap-1 rounded-full bg-surface-2 px-2 py-0.5 text-[11px] font-medium text-muted">
                            <BookOpen size={11} />
                            {livrosDaCategoria.length}
                            <ChevronDown
                              size={12}
                              className={`transition-transform ${expandida ? "rotate-180" : ""}`}
                            />
                          </span>
                        )}
                      </button>
                      {podeGerenciarBiblioteca && (
                        <div className="flex shrink-0 items-center gap-1">
                          <button onClick={() => abrirEdicao(cat)} aria-label="Editar" className="rounded-lg p-2 text-info transition-colors hover:bg-info/10">
                            <Pencil size={16} />
                          </button>
                          <button onClick={() => setCategoriaParaExcluir(cat)} aria-label="Excluir" className="rounded-lg p-2 text-danger transition-colors hover:bg-danger/10">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                    {expandida && livrosDaCategoria.length > 0 && (
                      <ul className="border-t border-border bg-surface-2 px-4 py-2">
                        {livrosDaCategoria.map((livro) => (
                          <li key={livro.id} className="py-1 text-xs text-muted">
                            · {livro.nome_livro}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <Paginacao paginaAtual={paginaAtual} totalPaginas={totalPaginas} onMudarPagina={setPaginaAtual} />
      </main>

      <Modal titulo={categoriaEmEdicao ? "Editar categoria" : "Nova categoria"} aberto={modalAberto} onClose={tentarFecharModal} protegerSaida={false} carregando={criarCategoria.isPending || atualizarCategoria.isPending}>
        <FormularioCategoria
          categoriaEmEdicao={categoriaEmEdicao}
          carregando={criarCategoria.isPending || atualizarCategoria.isPending}
          erro={erroFormulario}
          onDirtyChange={setFormularioSujo}
          onSubmit={handleSubmit}
        />
      </Modal>

      <ModalConfirmacao
        aberto={!!categoriaParaExcluir}
        titulo="Excluir categoria"
        mensagem={`Tem certeza que deseja excluir a categoria "${categoriaParaExcluir?.nome}"? Os livros vinculados a ela perderão a categoria.`}
        textoBotaoConfirmar="Excluir categoria"
        carregando={excluirCategoria.isPending}
        onConfirmar={confirmarExclusao}
        onCancelar={() => setCategoriaParaExcluir(null)}
      />

      <ModalSairSemSalvar
        aberto={confirmandoFechamento}
        onSair={fecharModalDeVerdade}
        onContinuar={() => setConfirmandoFechamento(false)}
      />
    </>
  );
}
